import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import { getSimulationRunStatus } from '@/lib/api';
import { useSimulationEvents, usePageVisibility } from '@/hooks';
import { LayoutContext } from './useLayoutContext';

/** Polling interval for run status when SSE is disconnected (3 seconds) */
const RUN_STATUS_POLL_INTERVAL = 3000;

interface LayoutProviderProps {
  children: ReactNode;
}

export function LayoutProvider({ children }: LayoutProviderProps) {
  const queryClient = useQueryClient();
  const { isVisible, onBecomeVisible } = usePageVisibility();

  // Selected simulation state
  const [selectedSimulationId, setSelectedSimulationId] = useState<string | null>(null);
  const [selectedSimulationName, setSelectedSimulationName] = useState<string | null>(null);

  // Results state
  const [hasResults, setHasResults] = useState(false);
  const [resultsCount, setResultsCount] = useState(0);

  // Simulation run state - keyed by simulation ID for coherence when switching simulations
  const [runningSimulationId, setRunningSimulationId] = useState<string | null>(null);
  const [simulationRunError, setSimulationRunError] = useState<string | null>(null);

  // Derived state: true only if the CURRENTLY SELECTED simulation is running.
  // This automatically becomes false when switching to a different simulation.
  const isSimulationRunning = runningSimulationId !== null && runningSimulationId === selectedSimulationId;

  // Version counter incremented when simulation results are available (for Results page to detect changes)
  const [resultsVersion, setResultsVersion] = useState(0);

  // Ref for run status polling interval
  const runStatusPollRef = useRef<NodeJS.Timeout | null>(null);

  // Track pending updates while tab is hidden (refs don't trigger re-renders)
  const pendingDataChangeRef = useRef(false);
  const pendingRunCompletedRef = useRef(false);

  // Perform actual data refetch (used immediately when visible, or deferred when hidden)
  const doRefetchData = useCallback(() => {
    if (selectedSimulationId) {
      queryClient.refetchQueries({ queryKey: queryKeys.simulation(selectedSimulationId) });
    }
  }, [queryClient, selectedSimulationId]);

  // SSE event handler - refetch query when data changes (visibility-aware)
  const handleDataChanged = useCallback(() => {
    if (isVisible) {
      doRefetchData();
    } else {
      // Tab is hidden - defer the refetch until it becomes visible
      pendingDataChangeRef.current = true;
    }
  }, [isVisible, doRefetchData]);

  // Run lifecycle SSE handlers.
  // These unconditionally update state because the SSE connection is already
  // scoped to selectedSimulationId — cross-simulation events cannot arrive.
  const handleRunStarted = useCallback(() => {
    if (selectedSimulationId) {
      setRunningSimulationId(selectedSimulationId);
      setSimulationRunError(null);
    }
  }, [selectedSimulationId]);

  const handleRunCompleted = useCallback(() => {
    setRunningSimulationId(null);
    setSimulationRunError(null);

    if (isVisible) {
      setResultsVersion((v) => v + 1);
    } else {
      pendingRunCompletedRef.current = true;
    }
  }, [isVisible]);

  const handleRunFailed = useCallback((data: { error: string }) => {
    setRunningSimulationId(null);
    setSimulationRunError(data.error);
  }, []);

  // Connect to SSE for the selected simulation
  const {
    connected: sseConnected,
    polling: ssePolling,
    switchToPolling,
    reconnect: reconnectSSE,
  } = useSimulationEvents(selectedSimulationId, {
    onChanged: handleDataChanged,
    onRefresh: handleDataChanged,
    onRunStarted: handleRunStarted,
    onRunCompleted: handleRunCompleted,
    onRunFailed: handleRunFailed,
  });

  // On mount / simulation change: check run status to restore running state after page refresh
  useEffect(() => {
    if (!selectedSimulationId) return;

    let cancelled = false;
    const currentSimId = selectedSimulationId; // Capture for closure
    getSimulationRunStatus(currentSimId)
      .then((status) => {
        if (cancelled) return;
        if (status.status === 'running') {
          // Only set if simulation hasn't changed during the async call
          setRunningSimulationId(currentSimId);
          setSimulationRunError(null);
        }
      })
      .catch(() => {
        // Ignore errors (e.g., 401 if not authenticated yet)
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSimulationId]);

  // Polling fallback: poll run status when running but SSE is disconnected
  useEffect(() => {
    // Clear any existing poll
    if (runStatusPollRef.current) {
      clearInterval(runStatusPollRef.current);
      runStatusPollRef.current = null;
    }

    // Only poll if the CURRENT simulation is running and SSE is disconnected
    if (!runningSimulationId || runningSimulationId !== selectedSimulationId || sseConnected) return;

    const pollingSimId = runningSimulationId; // Capture for closure
    runStatusPollRef.current = setInterval(() => {
      getSimulationRunStatus(pollingSimId)
        .then((status) => {
          // Guard: only update if we're still tracking this simulation
          setRunningSimulationId((current) => {
            if (current !== pollingSimId) return current; // Different simulation now, ignore
            if (status.status === 'completed' || status.status === 'failed') {
              if (status.status === 'failed') {
                setSimulationRunError(status.error ?? 'Unknown error');
              } else {
                setSimulationRunError(null);
              }
              return null; // Clear running state
            }
            return current; // Still running
          });
        })
        .catch(() => {
          // Ignore poll errors
        });
    }, RUN_STATUS_POLL_INTERVAL);

    return () => {
      if (runStatusPollRef.current) {
        clearInterval(runStatusPollRef.current);
        runStatusPollRef.current = null;
      }
    };
  }, [runningSimulationId, selectedSimulationId, sseConnected]);

  // Setters
  const setSelectedSimulation = useCallback((id: string | null, name: string | null) => {
    setSelectedSimulationId(id);
    setSelectedSimulationName(name);
  }, []);

  const setResultsInfo = useCallback((has: boolean, count: number) => {
    setHasResults(has);
    setResultsCount(count);
  }, []);

  // Explicitly mark a simulation run as started (called BEFORE POST /run).
  // This ensures the polling fallback activates even when SSE is disconnected.
  // Must be called before the API call to avoid a race condition where the SSE
  // completion event arrives before the HTTP response sets the running state.
  const markSimulationRunStarted = useCallback((simulationId: string) => {
    setRunningSimulationId(simulationId);
    setSimulationRunError(null);
  }, []);

  // Clear running state (used for error recovery when the API call fails
  // after optimistically marking the simulation as started).
  const clearSimulationRun = useCallback(() => {
    setRunningSimulationId(null);
    setSimulationRunError(null);
  }, []);

  // Process pending updates when tab becomes visible
  useEffect(() => {
    const cleanup = onBecomeVisible(() => {
      // Process pending data changes
      if (pendingDataChangeRef.current) {
        pendingDataChangeRef.current = false;
        doRefetchData();
      }

      // Process pending run completion
      if (pendingRunCompletedRef.current) {
        pendingRunCompletedRef.current = false;
        setResultsVersion((v) => v + 1);
      }
    });

    return cleanup;
  }, [onBecomeVisible, doRefetchData]);

  const value = {
    sseConnected,
    ssePolling,
    switchToPolling,
    reconnectSSE,
    selectedSimulationId,
    selectedSimulationName,
    setSelectedSimulation,
    hasResults,
    resultsCount,
    setResultsInfo,
    runningSimulationId,
    isSimulationRunning,
    simulationRunError,
    markSimulationRunStarted,
    clearSimulationRun,
    resultsVersion,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}
